import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import SiteFooter from '@/components/SiteFooter';
import { Reveal, SectionLabel, useSiteContent } from '@/components/Editorial';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight, Check, Info } from 'lucide-react';

const BUYING_TAGS  = ['Browse', 'Negotiate', 'Complete'];
const SELLING_TAGS = ['Submit',  'Exchange',  'Reward'];

const BUYING_IMAGES = [
  'https://www.thelistingroom.com/assets/images/discover.png',
  'https://www.thelistingroom.com/assets/images/discuss.png',
  'https://www.thelistingroom.com/assets/images/agree.png',
];

const SELLING_IMAGES = [
  'https://www.thelistingroom.com/assets/images/approved.png',
  'https://www.thelistingroom.com/assets/images/assist.png',
  'https://www.thelistingroom.com/assets/images/discounts.png',
];

export default function Guide() {
  const navigate = useNavigate();
  const c = useSiteContent('guide');
  const svc = useSiteContent('services');
  if (!c) return <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}><Navbar /></div>;

  return (
    <div style={{ backgroundColor: '#fff', color: '#000', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero — dark */}
      <section style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
        <div className="container-app py-28 md:py-36">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] mb-6 text-white/55">{c.hero.label}</p>
            <h1 className="font-bold mb-6" style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', lineHeight: 1.0, letterSpacing: '-0.03em', maxWidth: '880px' }}>
              {c.hero.heading}
            </h1>
            <p className="text-base text-white/65" style={{ maxWidth: '560px' }}>{c.hero.p}</p>
          </Reveal>
        </div>

      </section>

      {/* Buying — dark process flow with images */}
      <section style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
        <div className="container-app py-20 md:py-28">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs font-semibold tabular-nums text-white/50">(01)</span>
              <span className="w-8 h-px bg-white/25" />
              <span className="text-xs uppercase tracking-[0.25em] text-white/50">{c.buying.heading}</span>
            </div>
            <h2 className="font-bold mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1, letterSpacing: '-0.03em' }}>
              How to buy
            </h2>
            <p className="text-sm text-white/50 mb-14" style={{ maxWidth: '480px' }}>
              Three simple steps from discovery to ownership — no intermediary, no commission.
            </p>
          </Reveal>

          {/* Progress track */}
          <div className="hidden md:flex items-center mb-8" style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '2rem' }}>
            {c.buying.steps.map((_, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center text-xs font-bold" style={{ border: '1px solid rgba(255,255,255,0.4)' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">{BUYING_TAGS[i]}</span>
                </div>
                {i < c.buying.steps.length - 1 && (
                  <div className="flex-1 flex items-center px-6">
                    <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} />
                    <ArrowRight size={12} className="text-white/25 mx-1 flex-shrink-0" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-5" data-testid="guide-buying-steps">
            {c.buying.steps.map((s, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="group h-full overflow-hidden transition-all duration-300 cursor-default"
                  style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}>
                  {/* Step image */}
                  <div className="relative overflow-hidden" style={{ height: '220px' }}>
                    <img src={BUYING_IMAGES[i]} alt={s.title} className="w-full h-full object-contain transition-transform duration-700"
                      style={{ filter: 'grayscale(1) contrast(1.05)', transform: 'scale(1.02)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.07)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }} />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.7) 0%, rgba(10,10,10,0.1) 60%)' }} />
                    <span className="absolute bottom-4 right-4 text-4xl font-bold tabular-nums text-white/20">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  {/* Text */}
                  <div className="p-7">
                    <p className="text-[10px] uppercase tracking-[0.25em] mb-3 text-white/40">{BUYING_TAGS[i]}</p>
                    <h3 className="text-xl font-bold mb-3 text-white">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-white/55">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Selling — white process flow with images */}
      <section className="border-b" style={{ borderColor: '#E5E5E5', backgroundColor: '#fff' }}>
        <div className="container-app py-20 md:py-28">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs font-semibold tabular-nums">(02)</span>
              <span className="w-8 h-px" style={{ backgroundColor: '#000' }} />
              <span className="text-xs uppercase tracking-[0.25em]" style={{ color: '#666' }}>{c.selling.heading}</span>
            </div>
            <h2 className="font-bold mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1, letterSpacing: '-0.03em' }}>
              How to sell
            </h2>
            <p className="text-sm mb-14" style={{ color: '#666', maxWidth: '480px' }}>
              List your work, connect with buyers, and keep every penny — without the auction house middleman.
            </p>
          </Reveal>

          {/* Progress track */}
          <div className="hidden md:flex items-center mb-8" style={{ borderTop: '1px solid #000', paddingTop: '2rem' }}>
            {c.selling.steps.map((_, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center text-xs font-bold bg-black text-white">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.2em]" style={{ color: '#999' }}>{SELLING_TAGS[i]}</span>
                </div>
                {i < c.selling.steps.length - 1 && (
                  <div className="flex-1 flex items-center px-6">
                    <div className="flex-1 h-px" style={{ backgroundColor: '#E5E5E5' }} />
                    <ArrowRight size={12} className="mx-1 flex-shrink-0" style={{ color: '#CCC' }} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-5" data-testid="guide-selling-steps">
            {c.selling.steps.map((s, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="group h-full overflow-hidden transition-all duration-300 cursor-default"
                  style={{ border: '1px solid #E5E5E5' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#000'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E5E5'; }}>
                  {/* Step image */}
                  <div className="relative overflow-hidden" style={{ height: '220px', backgroundColor: '#F5F5F5' }}>
                    <img src={SELLING_IMAGES[i]} alt={s.title} className="w-full h-full object-contain transition-transform duration-700"
                      style={{ filter: 'grayscale(1) contrast(1.05)', transform: 'scale(1.02)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.07)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }} />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(255,255,255,0.4) 0%, transparent 60%)' }} />
                    <span className="absolute bottom-4 right-4 text-4xl font-bold tabular-nums" style={{ color: 'rgba(0,0,0,0.08)' }}>{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  {/* Text */}
                  <div className="p-7">
                    <p className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: '#999' }}>{SELLING_TAGS[i]}</p>
                    <h3 className="text-xl font-bold mb-3">{s.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#666' }}>{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={200}>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 p-6" style={{ border: '1px solid #E5E5E5', backgroundColor: '#FAFAFA' }}>
              <div className="flex-1">
                <p className="text-sm font-semibold mb-1">0% commission on every sale</p>
                <p className="text-xs" style={{ color: '#888' }}>You keep the full agreed price. Optional add-on services are the only fees — from £49 per listing.</p>
              </div>
              <button onClick={() => navigate('/register')}
                className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 text-xs font-semibold uppercase tracking-wider flex-shrink-0 transition-colors duration-200"
                style={{ backgroundColor: '#000', color: '#fff' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}>
                Start Selling <ArrowRight size={14} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Optional Services Comparison Table */}
      <section className="border-b" style={{ borderColor: '#E5E5E5', backgroundColor: '#fff' }}>
        <div className="container-app py-24 md:py-32">
          <Reveal>
            <SectionLabel index="03">Add-On Services</SectionLabel>
            <h2 className="font-bold mb-4" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 3rem)', lineHeight: 1.05, letterSpacing: '-0.03em', maxWidth: '760px' }}>
              Optional Listing Services
            </h2>
            <p className="text-sm mb-14" style={{ color: '#666', maxWidth: '560px' }}>
              Available from Messums Listings — select the level of support you need. Each package is a one-time fee per listing.
            </p>
          </Reveal>

          <Reveal delay={80}>
            <div className="overflow-x-auto" data-testid="guide-services-table">
              <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '560px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
                    <th className="py-4 px-6 text-left text-xs font-semibold uppercase tracking-[0.2em] w-1/2" style={{ borderRight: '1px solid rgba(255,255,255,0.1)' }}>
                      Package
                    </th>
                    {(svc?.packages || []).map((pkg, i) => (
                      <th key={i} className="py-4 px-6 text-center text-xs font-semibold uppercase tracking-[0.2em]" style={{ borderRight: i < (svc.packages.length - 1) ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                        {pkg.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(svc?.features || []).map((feat, idx) => {
                    const checks = feat.included || [];
                    const pkgCount = (svc?.packages || []).length;
                    return (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#FAFAFA' : '#fff', borderBottom: '1px solid #E5E5E5' }}>
                        <td className="py-4 px-6 text-sm" style={{ borderRight: '1px solid #E5E5E5' }}>
                          <span className="flex items-center gap-2">
                            <span>{feat.name}</span>
                            {feat.tooltip && (
                              <span title={feat.tooltip} className="cursor-help flex-shrink-0">
                                <Info size={13} style={{ color: '#C0C0C0' }} />
                              </span>
                            )}
                          </span>
                        </td>
                        {checks.map((has, ci) => (
                          <td key={ci} className="py-4 px-6 text-center" style={{ borderRight: ci < pkgCount - 1 ? '1px solid #E5E5E5' : 'none' }}>
                            {has && (
                              <span className="inline-flex items-center justify-center w-6 h-6 mx-auto" style={{ backgroundColor: '#000', borderRadius: '50%' }}>
                                <Check size={12} color="#fff" strokeWidth={2.5} />
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: '2px solid #000' }}>
                    <td className="py-5 px-6 text-sm font-semibold" style={{ borderRight: '1px solid #E5E5E5' }}>Price</td>
                    {(svc?.packages || []).map((pkg, i) => (
                      <td key={i} className="py-5 px-6 text-center" style={{ borderRight: i < (svc.packages.length - 1) ? '1px solid #E5E5E5' : 'none' }}>
                        <span className="text-xl font-bold tabular-nums">{pkg.price}</span>
                        <span className="block text-[10px] uppercase tracking-wider mt-0.5" style={{ color: '#999' }}>per listing</span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </Reveal>

          <Reveal delay={160}>
            <div className="mt-10 text-center">
              <button onClick={() => navigate('/register')} data-testid="guide-signup-cta"
                className="inline-flex items-center gap-3 px-10 py-4 text-sm font-semibold uppercase tracking-wider transition-colors duration-300"
                style={{ backgroundColor: '#000', color: '#fff' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}>
                Sign Up Today <ArrowRight size={16} />
              </button>
              <p className="text-xs mt-4" style={{ color: '#999' }}>Membership is free — fees only apply when you list or choose a service.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b" style={{ borderColor: '#E5E5E5' }}>
        <div className="container-app py-24 md:py-32 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal><SectionLabel index="04">FAQ</SectionLabel></Reveal>
            <Reveal delay={80}>
              <h2 className="font-bold" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
                {c.faq.heading}
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="text-sm mt-5" style={{ color: '#666' }}>
                Can't find your answer? <button onClick={() => navigate('/register')} className="font-semibold underline underline-offset-2 hover:opacity-60 transition-opacity">Join and ask us directly.</button>
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <Accordion type="single" collapsible className="w-full" style={{ borderTop: '1px solid #000' }}>
                {c.faq.items.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} style={{ borderColor: '#E5E5E5' }}>
                    <AccordionTrigger className="text-left text-base font-bold hover:no-underline py-6">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed pb-6" style={{ color: '#555' }}>{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
